// React
import { useState, useEffect } from "react";

// Third-Party
import MenuPopover from "@components/MenuPopover";
import ConfirmDialog from "@components/ConfirmDialog";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { ChevronRight, Trash } from "lucide-react";
import { useFormContext } from "react-hook-form";

// Internal
import { useChatHistory } from "../../hooks/useChatHistory";
import {
  MODE_OPTIONS,
  PROMPT_CATEGORIES,
  QUICK_ACTION_BUTTONS,
} from "../../utils/constants";
import { borderGeneralStyle } from "@utils/cssStyles";
import { capitalizeStr } from "@utils/stringFormat";
import {
  clearAllTextStyle,
  emptyMemoriesTextStyle,
  emptyStateTextStyle,
  getCategoryPillStyle,
  getRowDeleteIconButtonStyle,
  getTabButtonStyle,
  memoryRowStyle,
  memoryTextStyle,
  memoryWhenStyle,
  optionDescriptionStyle,
  optionTitleStyle,
  getOptionRowStyle,
  preferencesMenuPopoverStyle,
  preferencesSidebarFooterStyle,
  preferencesSidebarStyle,
  promptCategoriesWrapperStyle,
  promptRowStyle,
  promptRowTextStyle,
  sectionDividerStyle,
  sectionLabelStyle,
} from "../../styles/askDostStyles";

// Types
import {
  IAskDostPreferencesMenuProps,
  IDisplayPrompt,
  IPendingDelete,
  ISendChatMessage,
  TAnswerMode,
  TPreferencesTab,
  TPromptCategory,
} from "../../types";
import { AskDostIntegrationsTab } from "../connectors/AskDostIntegrationsTab";

export type { TAnswerMode };

export const AskDostPreferencesMenu: React.FC<IAskDostPreferencesMenuProps> = ({
  anchorEl,
  onClose,
  palette,
  translate,
  mode,
  onModeChange,
}) => {
  const [tab, setTab] = useState<TPreferencesTab>("mode");
  const [activePromptCategory, setActivePromptCategory] =
    useState<TPromptCategory>("all");
  const [pendingDelete, setPendingDelete] = useState<IPendingDelete | null>(
    null,
  );
  const { setValue } = useFormContext<ISendChatMessage>();
  const {
    savedPrompts,
    removeSavedPrompt,
    memories,
    removeMemory,
    clearMemories,
    fetchMemories,
    fetchSavedPrompts,
  } = useChatHistory({ translate });

  useEffect(() => {
    if (Boolean(anchorEl)) {
      if (tab === "memories") {
        fetchMemories();
      } else if (tab === "prompts") {
        fetchSavedPrompts();
      }
    }
  }, [anchorEl, tab, fetchMemories, fetchSavedPrompts]);

  const handlePromptSelect = (prompt: string) => {
    setValue("message", prompt);
    onClose();
  };

  const deleteDialogTitleKey =
    pendingDelete?.kind === "memory"
      ? "delete_memory_title"
      : pendingDelete?.kind === "prompt"
      ? "delete_prompt_title"
      : "clear_all_memories_title";

  const deleteDialogContentKey =
    pendingDelete?.kind === "memory"
      ? "delete_memory_message"
      : pendingDelete?.kind === "prompt"
      ? "delete_prompt_message"
      : "clear_all_memories_message";

  const handleConfirmPendingDelete = () => {
    if (!pendingDelete) return;
    if (pendingDelete.kind === "memory" && pendingDelete.id) {
      removeMemory(pendingDelete.id);
    } else if (pendingDelete.kind === "prompt" && pendingDelete.id) {
      removeSavedPrompt(pendingDelete.id);
    } else if (pendingDelete.kind === "clear_all_memories") {
      clearMemories();
    }
    setPendingDelete(null);
  };

  const userSavedPromptItems: IDisplayPrompt[] = (
    Array.isArray(savedPrompts) ? savedPrompts : []
  ).map((prompt) => ({
    id: prompt.id,
    text: prompt.text,
    category: "saved_by_you",
    isUserSaved: true,
  }));

  const systemPredefinedPromptItems: IDisplayPrompt[] =
    QUICK_ACTION_BUTTONS.flatMap((button) =>
      button.promptKeys.map((promptKey) => ({
        id: promptKey,
        text: translate(promptKey),
        category: button.id as TPromptCategory,
        isUserSaved: false,
      })),
    );

  const displayedPrompts: IDisplayPrompt[] =
    activePromptCategory === "all"
      ? [...userSavedPromptItems, ...systemPredefinedPromptItems]
      : activePromptCategory === "saved_by_you"
      ? userSavedPromptItems
      : systemPredefinedPromptItems.filter(
          (item) => item.category === activePromptCategory,
        );

  const tabButton = (key: TPreferencesTab, label: string) => (
    <Box
      onClick={() => setTab(key)}
      sx={getTabButtonStyle(palette, tab === key)}
    >
      {label}
      <ChevronRight color={palette.text.disabled} size={14} />
    </Box>
  );

  const safeMemories = Array.isArray(memories) ? memories : [];

  return (
    <>
      <MenuPopover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        disabledArrow
        sx={preferencesMenuPopoverStyle}
      >
        <Box sx={preferencesSidebarStyle}>
          <Box sx={{ flex: 1 }}>
            {tabButton("mode", translate("mode"))}
            {tabButton("prompts", translate("saved_prompts"))}
            {tabButton("memories", translate("memories"))}
            {tabButton("integrations", translate("connectors"))}
          </Box>

          <Typography
            variant="caption"
            sx={preferencesSidebarFooterStyle(palette)}
          >
            {capitalizeStr(translate("mode"))}.{" "}
            {translate(
              mode === "fast" ? "mode_fast_name" : "mode_thinking_name",
            )}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, overflowY: "auto", py: 2 }}>
          {tab === "mode" && (
            <Stack spacing={2}>
              <Typography
                variant="caption"
                sx={{ ...sectionLabelStyle(palette), px: 2 }}
              >
                {translate("answering_mode")}
              </Typography>
              <Box sx={{ ...sectionDividerStyle, mb: 2 }} />

              <Stack spacing={2} sx={{ px: 2 }}>
                {MODE_OPTIONS.map((option) => {
                  const isSelected = option.key === mode;
                  return (
                    <Box
                      key={option.key}
                      onClick={() => onModeChange(option.key)}
                      sx={{
                        ...borderGeneralStyle,
                        ...getOptionRowStyle(palette, isSelected),
                        "&:hover": {
                          borderColor: palette.secondary.dark,
                          backgroundColor: palette.text.input,
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={optionTitleStyle(palette)}
                      >
                        {translate(option.nameKey)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={optionDescriptionStyle(palette)}
                      >
                        {translate(option.descKey)}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Stack>
          )}

          {tab === "prompts" && (
            <Stack spacing={0}>
              <Stack
                direction="row"
                spacing={1}
                sx={promptCategoriesWrapperStyle}
              >
                {PROMPT_CATEGORIES.map((category) => {
                  const isActive = category.key === activePromptCategory;
                  return (
                    <Box
                      key={category.key}
                      onClick={() => setActivePromptCategory(category.key)}
                      sx={getCategoryPillStyle(palette, isActive)}
                    >
                      {translate(category.labelKey)}
                    </Box>
                  );
                })}
              </Stack>

              <Box sx={sectionDividerStyle} />

              <Typography
                variant="caption"
                sx={{
                  ...sectionLabelStyle(palette),
                  color: palette.secondary.dark,
                  px: 2,
                  pt: 1,
                }}
              >
                {translate("dost_suggestions")}
              </Typography>

              <Box sx={{ px: 2 }}>
                {displayedPrompts.length === 0 ? (
                  <Typography
                    variant="caption"
                    sx={emptyStateTextStyle(palette)}
                  >
                    {translate("no_saved_prompts_yet")}
                  </Typography>
                ) : (
                  displayedPrompts.map((item) => (
                    <Stack
                      key={item.id}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={promptRowStyle(palette)}
                    >
                      <Typography
                        variant="body2"
                        onClick={() => handlePromptSelect(item.text)}
                        sx={promptRowTextStyle(palette)}
                      >
                        {item.text}
                      </Typography>
                      {item.isUserSaved && (
                        <IconButton
                          size="small"
                          title={translate("remove_saved_prompt")}
                          onClick={() =>
                            setPendingDelete({ kind: "prompt", id: item.id })
                          }
                          sx={getRowDeleteIconButtonStyle(palette)}
                        >
                          <Trash size={18} strokeWidth="1.5px" />
                        </IconButton>
                      )}
                    </Stack>
                  ))
                )}
              </Box>
            </Stack>
          )}

          {tab === "memories" && (
            <Stack spacing={1}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 2 }}
              >
                <Typography variant="caption" sx={sectionLabelStyle(palette)}>
                  {translate("saved_by_dost")}
                </Typography>

                {safeMemories.length > 0 && (
                  <Typography
                    variant="caption"
                    onClick={() =>
                      setPendingDelete({ kind: "clear_all_memories" })
                    }
                    sx={clearAllTextStyle(palette)}
                  >
                    {translate("clear_all")}
                  </Typography>
                )}
              </Stack>
              <Box sx={{ ...sectionDividerStyle, mb: 1 }} />

              <Box sx={{ px: 2 }}>
                {safeMemories.length === 0 ? (
                  <Typography
                    variant="caption"
                    sx={emptyMemoriesTextStyle(palette)}
                  >
                    {translate("no_memories_yet")}
                  </Typography>
                ) : (
                  safeMemories.map((memory) => (
                    <Stack
                      key={memory.id}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                      sx={memoryRowStyle(palette)}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={memoryTextStyle(palette)}
                        >
                          {memory.text}
                        </Typography>
                        {memory.when && (
                          <Typography
                            variant="caption"
                            sx={memoryWhenStyle(palette)}
                          >
                            {memory.when}
                          </Typography>
                        )}
                      </Box>
                      <IconButton
                        size="small"
                        title={translate("delete_memory")}
                        onClick={() =>
                          setPendingDelete({ kind: "memory", id: memory.id })
                        }
                        sx={getRowDeleteIconButtonStyle(palette)}
                      >
                        <Trash size={18} strokeWidth="1.5px" />
                      </IconButton>
                    </Stack>
                  ))
                )}
              </Box>
            </Stack>
          )}

          {tab === "integrations" && (
            <AskDostIntegrationsTab palette={palette} translate={translate} />
          )}
        </Box>
      </MenuPopover>

      <ConfirmDialog
        open={!!pendingDelete}
        title={translate(deleteDialogTitleKey)}
        content={translate(deleteDialogContentKey)}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleConfirmPendingDelete}
      />
    </>
  );
};
